import { createSelector } from 'reselect';

import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';

import { toSelectOption, toSelectOptions, toSelectOptionsWithProvider } from '~/src/helpers/form';
import { TState } from '~/src/store';

const getCities = ({ state }: { state: TState }) => {
    const { citiesList } = state?.city || {};
    return citiesList || [];
};

const getCity = ({ state }: { state: TState }): ICurrentCity => {
    const { currentCity } = state?.city || {};
    return currentCity || null;
};

export const getHostname = (state: TState): string => {
    const { hostname } = state?.city?.currentCity || window.location.hostname;
    return hostname || '';
};

export const getCurrentCity = (state: TState): ICurrentCity => {
    return getCity({ state });
};

export function getCurrentCityIn(state: TState): string | undefined {
    return getCurrentCity(state)?.cityIn;
}

export const citiesListForHeader = createSelector(
    [getCities],
    cities => cities.map(city => ({
        cityId: city.city_id,
        domain: city.domain,
        name: city.name,
        phone: city.phone_code === '0' ? city.sale_phone : `+7 (${city.phone_code}) ${city.sale_phone}`,
        callCenter: city.call_center_phone.length > 10 ? city.call_center_phone : `+7 (${city.phone_code}) ${city.call_center_phone}`,
        billingHost: city.billing_host,
        cityIn: city.pred_name,
        prefixAgreement: city.prefix_agreement,
    })),
);

const getOptionKeys = ({ optionKeys }: { optionKeys: Record<string, string> }) => optionKeys;

export const selectCurrentCityOptions = createSelector(
    [getCity, getOptionKeys],
    (object, keys) => toSelectOption({ object, ...keys }),
);

/**
    Возвращает массив объектов типа {value: 'angarsk', label: 'Ангарск'} из списка городов
*/
export const selectCitiesOptions = createSelector(
    [getCities, getOptionKeys],
    (array, keys) => toSelectOptions({ array, keys }),
);
/**
    Возвращает массив объектов типа {value: { domain:'angarsk', providerId: 47}, label: 'Ангарск'} из списка городов
*/
export const selectCitiesOptionWithProvider = createSelector(
    [getCities, getOptionKeys],
    (array, keys) => toSelectOptionsWithProvider({ array, keys }),
);

export function selectCurrentCityDomain(state: TState): string {
    return state?.city?.currentCity?.domain;
}

/**
 * Получение электронной почты для контактов.
 * todo на данный момент нет источника получения почты с бека (в модели города в контенте отсутствует такое поле)
 * todo когда появится - доработать селектор
 * @param {TState} state
 * @return {string}
 */
export function selectContactEmail(state: TState): string {
    // для кобренда Rinet (Кроникс Плюс)
    if (state?.city?.provider.providerId === 41) {
        return 'client_rinetdomru@domru.ru';
    } else {
        return 'help@domru.ru';
    }
}

/**
 * Получение текущего названия компании. Вернет 'Дом.ру' если значение не задано в контенте.
 * @param {TState} state
 * @return {string}
 */
export function selectCompanyName(state: TState): string {
    return state.city.currentCity.companyName || 'Дом.ру';
}

export interface ICityLogo {
    /**
     * Стандартный, полный логотип.
     */
    full: string,
    /**
     * Сокращенный вариант логотипа.
     */
    narrow: string,
}

/**
 * Получение путей до логотипов города.
 * @param {TState} state
 * @return {ICityLogo}
 */
export function selectCityLogo(state: TState): ICityLogo {
    const fallBackLogo = '/static/images/domru.svg';

    return {
        full: state?.city?.currentCity?.logoFull || fallBackLogo,
        narrow: state?.city?.currentCity?.logo_narrow || fallBackLogo,
    };
}

/**
 * Получение названия юридического лица города.
 * @param {TState} state
 * @return {string}
 */
export function selectLegalEntity(state: TState): string {
    return state?.city?.currentCity?.legalEntity ? state.city.currentCity.legalEntity : 'АО «ЭР-Телеком Холдинг»';
}

export const selectCurrentCityPhone = createSelector(
    getCurrentCity,
    (city) => {
        const { callCenter, phoneCode } = city || {};
        const callCenterNumber = callCenter?.replace(/\D/g, '');
        const href = `tel:+${callCenterNumber}`;
        const hasPhoneData = callCenterNumber?.replace(`7${phoneCode}`, '') !== '';

        return { callCenterNumber, href, hasPhoneData, callCenter };
    },
);
